import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiSearch, FiFilter, FiDownload, FiFileText } from 'react-icons/fi';
import './ProposalKegiatan.css';

function ProposalKegiatan() {
  const [proposals, setProposals] = useState([]);
  const [filteredProposals, setFilteredProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    nomor_proposal: '',
    judul: '',
    jenis_proposal: 'Kegiatan Mahasiswa',
    pengusul: {
      nama: '',
      nim_nip: '',
      prodi: ''
    },
    tanggal_pengajuan: '',
    anggaran_disetujui: 0,
    deskripsi: '',
    dokumen_proposal: ''
  });

  useEffect(() => {
    fetchProposals();
  }, []);

  useEffect(() => {
    let filtered = [...proposals];

    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.judul.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.nomor_proposal.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.pengusul.nama.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus) {
      filtered = filtered.filter(p => p.status === filterStatus);
    }

    setFilteredProposals(filtered);
  }, [proposals, searchTerm, filterStatus]);

  const fetchProposals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/proposals', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Response from API:', response.data);
      
      // Backend mengembalikan array langsung, bukan dalam object data
      const allProposals = Array.isArray(response.data) ? response.data : [];
      
      // Filter hanya proposal Kegiatan Mahasiswa
      const kegiatanMahasiswa = allProposals.filter(
        p => p.jenis_proposal === 'Kegiatan Mahasiswa'
      );
      
      console.log('Filtered Kegiatan Mahasiswa:', kegiatanMahasiswa);
      
      setProposals(kegiatanMahasiswa);
      setFilteredProposals(kegiatanMahasiswa);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal memuat data proposal');
      console.error('Error fetching proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('pengusul.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        pengusul: {
          ...formData.pengusul,
          [field]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const submitData = {
        ...formData,
        anggaran_disetujui: Number(formData.anggaran_disetujui)
      };

      if (isEditMode && selectedProposal) {
        await axios.put(`/api/proposals/${selectedProposal._id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proposal berhasil diupdate');
      } else {
        await axios.post('/api/proposals', submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proposal berhasil ditambahkan');
      }

      setShowModal(false);
      resetForm();
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menyimpan proposal');
      console.error(error);
    }
  };

  const handleEdit = (proposal) => {
    setSelectedProposal(proposal);
    setFormData({
      nomor_proposal: proposal.nomor_proposal,
      judul: proposal.judul,
      jenis_proposal: proposal.jenis_proposal,
      pengusul: proposal.pengusul,
      tanggal_pengajuan: proposal.tanggal_pengajuan?.split('T')[0] || '',
      anggaran_disetujui: proposal.anggaran_disetujui,
      deskripsi: proposal.deskripsi || '',
      dokumen_proposal: proposal.dokumen_proposal || ''
    });
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus proposal ini?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/proposals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proposal berhasil dihapus');
      fetchProposals();
    } catch (error) {
      toast.error('Gagal menghapus proposal');
      console.error(error);
    }
  };

  const handleViewDetail = (proposal) => {
    setSelectedProposal(proposal);
    setShowDetailModal(true);
  };

  const resetForm = () => {
    setFormData({
      nomor_proposal: '',
      judul: '',
      jenis_proposal: 'Kegiatan Mahasiswa',
      pengusul: {
        nama: '',
        nim_nip: '',
        prodi: ''
      },
      tanggal_pengajuan: '',
      anggaran_disetujui: 0,
      deskripsi: '',
      dokumen_proposal: ''
    });
    setIsEditMode(false);
    setSelectedProposal(null);
  };

  const handleAddNew = () => {
    resetForm();
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'Pending': { class: 'warning', text: 'Pending' },
      'Disetujui': { class: 'success', text: 'Disetujui' },
      'Ditolak': { class: 'danger', text: 'Ditolak' },
      'Selesai': { class: 'info', text: 'Selesai' }
    };
    const statusInfo = statusMap[status] || { class: 'secondary', text: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Memuat data...</p>
      </div>
    );
  }

  return (
    <div className="proposal-kegiatan-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Proposal Kegiatan Mahasiswa</h1>
          <p>
            Kelola proposal kegiatan kemahasiswaan
            {proposals.length > 0 && ` • ${proposals.length} proposal`}
          </p>
        </div>
        <button className="btn-primary" onClick={handleAddNew}>
          <FiPlus /> Tambah Proposal
        </button>
      </div>

      <div className="filter-section">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Cari berdasarkan judul, nomor, atau pengusul..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-box">
          <FiFilter className="filter-icon" />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">Semua Status</option>
            <option value="Pending">Pending</option>
            <option value="Disetujui">Disetujui</option>
            <option value="Ditolak">Ditolak</option>
            <option value="Selesai">Selesai</option>
          </select>
        </div>
      </div>

      <div className="proposals-table-container">
        <table className="proposals-table">
          <thead>
            <tr>
              <th>No</th>
              <th>Nomor Proposal</th>
              <th>Judul</th>
              <th>Pengusul</th>
              <th>Tanggal Pengajuan</th>
              <th>Anggaran Disetujui</th>
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filteredProposals.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center">
                  <div className="empty-state">
                    <FiFileText className="empty-icon" />
                    <p>
                      {proposals.length === 0 
                        ? 'Belum ada proposal kegiatan mahasiswa'
                        : 'Tidak ada data yang sesuai dengan filter'}
                    </p>
                    {proposals.length === 0 && (
                      <button className="btn-primary" onClick={handleAddNew} style={{ marginTop: '1rem' }}>
                        <FiPlus /> Tambah Proposal Pertama
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filteredProposals.map((proposal, index) => (
                <tr key={proposal._id}>
                  <td>{index + 1}</td>
                  <td>{proposal.nomor_proposal}</td>
                  <td className="proposal-title">{proposal.judul}</td>
                  <td>
                    <div className="pengusul-info">
                      <strong>{proposal.pengusul.nama}</strong>
                      <small>{proposal.pengusul.nim_nip}</small>
                    </div>
                  </td>
                  <td>{formatDate(proposal.tanggal_pengajuan)}</td>
                  <td className="amount">{formatCurrency(proposal.anggaran_disetujui)}</td>
                  <td>{getStatusBadge(proposal.status)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-info"
                        onClick={() => handleViewDetail(proposal)}
                        title="Lihat Detail"
                      >
                        <FiEye />
                      </button>
                      <button
                        className="btn-icon btn-warning"
                        onClick={() => handleEdit(proposal)}
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDelete(proposal._id)}
                        title="Hapus"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditMode ? 'Edit Proposal' : 'Tambah Proposal Baru'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Nomor Proposal *</label>
                    <input
                      type="text"
                      name="nomor_proposal"
                      value={formData.nomor_proposal}
                      onChange={handleInputChange}
                      placeholder="Contoh: PROP/KM/2024/001"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Tanggal Pengajuan *</label>
                    <input
                      type="date"
                      name="tanggal_pengajuan"
                      value={formData.tanggal_pengajuan}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Judul Kegiatan *</label>
                  <input
                    type="text"
                    name="judul"
                    value={formData.judul}
                    onChange={handleInputChange}
                    placeholder="Masukkan judul kegiatan"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Jenis Proposal</label>
                  <input
                    type="text"
                    value="Kegiatan Mahasiswa"
                    disabled
                    className="disabled-input"
                  />
                </div>

                <div className="form-section-title">Data Pengusul</div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Nama Pengusul *</label>
                    <input
                      type="text"
                      name="pengusul.nama"
                      value={formData.pengusul.nama}
                      onChange={handleInputChange}
                      placeholder="Nama lengkap pengusul"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>NIM *</label>
                    <input
                      type="text"
                      name="pengusul.nim_nip"
                      value={formData.pengusul.nim_nip}
                      onChange={handleInputChange}
                      placeholder="NIM pengusul"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Program Studi *</label>
                  <select
                    name="pengusul.prodi"
                    value={formData.pengusul.prodi}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">-- Pilih Program Studi --</option>
                    <option value="S1 Keperawatan">S1 Keperawatan</option>
                    <option value="D3 Keperawatan">D3 Keperawatan</option>
                    <option value="Profesi Ners">Profesi Ners</option>
                    <option value="S1 Kebidanan">S1 Kebidanan</option>
                    <option value="D3 Kebidanan">D3 Kebidanan</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Anggaran yang Diajukan *</label>
                  <input
                    type="number"
                    name="anggaran_disetujui"
                    value={formData.anggaran_disetujui}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Deskripsi Kegiatan</label>
                  <textarea
                    name="deskripsi"
                    value={formData.deskripsi}
                    onChange={handleInputChange}
                    placeholder="Deskripsi singkat tentang kegiatan"
                    rows="4"
                  ></textarea>
                </div>

                <div className="form-group">
                  <label>Link/Path Dokumen Proposal</label>
                  <input
                    type="text"
                    name="dokumen_proposal"
                    value={formData.dokumen_proposal}
                    onChange={handleInputChange}
                    placeholder="URL atau path dokumen proposal"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Batal
                </button>
                <button type="submit" className="btn-primary">
                  {isEditMode ? 'Update' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Detail */}
      {showDetailModal && selectedProposal && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content modal-detail" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detail Proposal</h2>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Informasi Proposal</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Nomor Proposal</label>
                    <p>{selectedProposal.nomor_proposal}</p>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <p>{getStatusBadge(selectedProposal.status)}</p>
                  </div>
                  <div className="detail-item">
                    <label>Judul Kegiatan</label>
                    <p>{selectedProposal.judul}</p>
                  </div>
                  <div className="detail-item">
                    <label>Jenis Proposal</label>
                    <p>{selectedProposal.jenis_proposal}</p>
                  </div>
                  <div className="detail-item">
                    <label>Tanggal Pengajuan</label>
                    <p>{formatDate(selectedProposal.tanggal_pengajuan)}</p>
                  </div>
                  {selectedProposal.tanggal_disetujui && (
                    <div className="detail-item">
                      <label>Tanggal Disetujui</label>
                      <p>{formatDate(selectedProposal.tanggal_disetujui)}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h3>Data Pengusul</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Nama</label>
                    <p>{selectedProposal.pengusul.nama}</p>
                  </div>
                  <div className="detail-item">
                    <label>NIM</label>
                    <p>{selectedProposal.pengusul.nim_nip}</p>
                  </div>
                  <div className="detail-item">
                    <label>Program Studi</label>
                    <p>{selectedProposal.pengusul.prodi}</p>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Anggaran</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Anggaran Disetujui</label>
                    <p className="amount-large">{formatCurrency(selectedProposal.anggaran_disetujui)}</p>
                  </div>
                  <div className="detail-item">
                    <label>Anggaran Terpakai</label>
                    <p className="amount-large">{formatCurrency(selectedProposal.anggaran_terpakai || 0)}</p>
                  </div>
                  <div className="detail-item">
                    <label>Anggaran Sisa</label>
                    <p className="amount-large">{formatCurrency(selectedProposal.anggaran_sisa || selectedProposal.anggaran_disetujui)}</p>
                  </div>
                </div>
              </div>

              {selectedProposal.deskripsi && (
                <div className="detail-section">
                  <h3>Deskripsi</h3>
                  <p className="description-text">{selectedProposal.deskripsi}</p>
                </div>
              )}

              {selectedProposal.dokumen_proposal && (
                <div className="detail-section">
                  <h3>Dokumen</h3>
                  <a 
                    href={selectedProposal.dokumen_proposal} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="document-link"
                  >
                    <FiDownload /> Download Dokumen Proposal
                  </a>
                </div>
              )}

              {selectedProposal.keterangan && (
                <div className="detail-section">
                  <h3>Keterangan</h3>
                  <p className="description-text">{selectedProposal.keterangan}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetailModal(false)}>
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProposalKegiatan;
